import React, { useEffect, useState } from 'react';

import { RadioOrSelectOption } from '../../../../../../types/common.types';
import { AttendanceSession, AttendanceSessionTemplate } from '../../../../../../types/entity.types';

import useTimeOptions from '../../../../../../hooks/use-time-options/useTimeOptions';
import useToaster from '../../../../../../hooks/use-toaster/useToaster';

import { ARCHIVED, ERROR } from '../../../../../../utils/constants';
import {
  convertDateToTimeString,
  convertTimeStringToDate,
  mapStringOptionsToObjectOptions
} from '../../../../../../utils/util';

import Button from '../../../../../common/button/Button';
import Checkbox from '../../../../../common/input/checkbox/Checkbox';
import DatePicker from '../../../../../common/input/date-picker/DatePicker';
import Dropdown from '../../../../../common/input/dropdown/Dropdown';
import NumberPicker from '../../../../../common/input/number-picker/NumberPicker';
import TextInput from '../../../../../common/input/text-input/TextInput';
import Modal from '../../../../../common/modal/Modal';

import './accountability-attendance-sessions-modal.scss';

const REOCCURRENCE_OPTIONS: RadioOrSelectOption[] = [
  { label: 'Once-Off', value: 'once-off' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' }
];

type Props = {
  status: string;
  show: boolean;
  close: () => void;
  session?: AttendanceSession;
  updateSession: (session: Partial<AttendanceSession>) => void;
  generateSessions: (sessionTemplate: AttendanceSessionTemplate, onSuccess: () => void) => void;
};

const AccountabilityAttendanceSessionsModal = ({
  status,
  show,
  close,
  session,
  updateSession,
  generateSessions
}: Props) => {
  const [templateName, setTemplateName] = useState('');
  const [reoccurrencePattern, setReoccurrencePattern] = useState<RadioOrSelectOption>({
    label: 'Once-Off',
    value: 'once-off'
  });
  const [startTime, setStartTime] = useState<string>();
  const [endTime, setEndTime] = useState<string>();
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [dailyFrequency, setDailyFrequency] = useState('1');
  const [everyWeekday, setEveryWeekday] = useState(false);
  const [weeklyFrequency, setWeeklyFrequency] = useState('1');
  const [monday, setMonday] = useState(false);
  const [tuesday, setTuesday] = useState(false);
  const [wednesday, setWednesday] = useState(false);
  const [thursday, setThursday] = useState(false);
  const [friday, setFriday] = useState(false);
  const [saturday, setSaturday] = useState(false);
  const [sunday, setSunday] = useState(false);
  const [monthlyFrequencyOne, setMonthlyFrequencyOne] = useState('1');
  const [monthlyFrequencyTwo, setMonthlyFrequencyTwo] = useState('1');
  const [specificDayInMonth, setSpecificDayInMonth] = useState(true);
  const [dayInMonth, setDayInMonth] = useState('1');
  const [monthlyStringOne, setMonthlyStringOne] = useState('First');
  const [monthlyStringTwo, setMonthlyStringTwo] = useState('Monday');
  const [name, setName] = useState('');
  const { showToaster } = useToaster();

  useEffect(() => {
    setStartDate(undefined);
    setEndDate(undefined);
  }, [reoccurrencePattern]);

  useEffect(() => {
    if (session) {
      setName(session.name ? session.name : '');
      setStartTime(convertDateToTimeString(session.startTime));
      setEndTime(convertDateToTimeString(session.endTime));

      if (!session.startDate) return;

      setStartDate(new Date(session.startDate));
    } else {
      setName('');
      setStartTime(undefined);
      setEndTime(undefined);
      setStartDate(undefined);
    }
  }, [session, show]);

  const [startTimeOptions, endTimeOptions] = useTimeOptions(startTime, endTime);

  const resetFields = () => {
    setReoccurrencePattern({ label: 'Once-Off', value: 'once-off' });
    setStartTime(undefined);
    setEndTime(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    setDailyFrequency('1');
    setEveryWeekday(false);
    setWeeklyFrequency('1');
    setMonday(false);
    setTuesday(false);
    setWednesday(false);
    setThursday(false);
    setFriday(false);
    setSaturday(false);
    setSunday(false);
    setMonthlyFrequencyOne('1');
    setMonthlyFrequencyTwo('1');
    setSpecificDayInMonth(true);
    setDayInMonth('1');
    setMonthlyStringOne('First');
    setMonthlyStringTwo('Monday');
    setName('');
  };

  const closeModal = () => {
    close();
    resetFields();
  };

  const buildSessionTemplate = () => {
    if (!startTime) {
      showToaster(ERROR, 'Please select a start time');
      return;
    }

    if (!endTime) {
      showToaster(ERROR, 'Please select an end time');
      return;
    }

    let weeklyReoccurrenceDays = '';
    if (reoccurrencePattern.value === 'weekly') {
      if (monday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}monday;`;
      }

      if (tuesday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}tuesday;`;
      }

      if (wednesday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}wednesday;`;
      }

      if (thursday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}thursday;`;
      }

      if (friday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}friday;`;
      }

      if (saturday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}saturday;`;
      }

      if (sunday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}sunday;`;
      }

      weeklyReoccurrenceDays = weeklyReoccurrenceDays.slice(0, -1);
    }

    const sessionTemplate: AttendanceSessionTemplate = {
      name: templateName,
      dailyReoccurrence: everyWeekday ? undefined : dailyFrequency,
      everyWeekDay: everyWeekday,
      externalReference: undefined,
      monthlyReoccurrence: specificDayInMonth ? monthlyFrequencyOne : monthlyFrequencyTwo,
      dayOccurrenceInMonth: specificDayInMonth ? dayInMonth : undefined,
      monthlyReoccurrenceDay: specificDayInMonth
        ? undefined
        : `${monthlyStringOne.toLowerCase()};${monthlyStringTwo.toLowerCase()}`,
      weeklyReoccurrence: weeklyFrequency,
      weeklyReoccurrenceDays,
      reoccurrencePattern: reoccurrencePattern.value,
      startTime: convertTimeStringToDate(startTime),
      endTime: convertTimeStringToDate(endTime),
      startDate: startDate,
      endDate: endDate
    };

    return sessionTemplate;
  };

  const handleSaveClick = () => {
    if (session) {
      const sessionToUpdate: Partial<AttendanceSession> = {
        externalReference: session.externalReference,
        startTime: convertTimeStringToDate(startTime),
        endTime: convertTimeStringToDate(endTime),
        startDate: startDate,
        name
      };
      updateSession(sessionToUpdate);
      closeModal();
    } else {
      const sessionTemplate = buildSessionTemplate();

      if (sessionTemplate) {
        generateSessions(sessionTemplate, closeModal);
      }
    }
  };

  const renderDailyOptions = () => (
    <div className="accountability-attendance-sessions-modal__daily">
      <div className="accountability-attendance-sessions-modal__daily__option">
        <div
          className="accountability-attendance-sessions-modal__radio-button accountability-attendance-sessions-modal__radio-button--outer"
          onClick={() => setEveryWeekday(false)}
          data-testid="daily-radio-option-one-outer"
        >
          {!everyWeekday && (
            <div
              className="accountability-attendance-sessions-modal__radio-button accountability-attendance-sessions-modal__radio-button--inner"
              data-testid="daily-radio-option-one-inner"
            />
          )}
        </div>
        <p>Every</p>
        <NumberPicker value={dailyFrequency} onChange={(value) => setDailyFrequency(value.toString())} hideArrows />
        <p>day(s)</p>
      </div>
      <div className="accountability-attendance-sessions-modal__daily__option">
        <div
          className="accountability-attendance-sessions-modal__radio-button accountability-attendance-sessions-modal__radio-button--outer"
          onClick={() => setEveryWeekday(true)}
          data-testid="daily-radio-option-two-outer"
        >
          {everyWeekday && (
            <div
              className="accountability-attendance-sessions-modal__radio-button accountability-attendance-sessions-modal__radio-button--inner"
              data-testid="daily-radio-option-two-inner"
            />
          )}
        </div>
        <p>Every Weekday</p>
      </div>
    </div>
  );

  const renderWeeklyOptions = () => (
    <div className="accountability-attendance-sessions-modal__weekly">
      <div className="accountability-attendance-sessions-modal__weekly__option">
        <p>Every</p>
        <NumberPicker value={weeklyFrequency} onChange={(value) => setWeeklyFrequency(value.toString())} hideArrows />
        <p>week(s) on:</p>
      </div>
      <div className="accountability-attendance-sessions-modal__weekly__days">
        <Checkbox label="Monday" name="monday-checkbox" value={monday} onChange={(value) => setMonday(value)} left />
        <Checkbox
          label="Tuesday"
          name="tuesday-checkbox "
          value={tuesday}
          onChange={(value) => setTuesday(value)}
          left
        />
        <Checkbox
          label="Wednesday"
          name="wednesday-checkbox "
          value={wednesday}
          onChange={(value) => setWednesday(value)}
          left
        />
        <Checkbox
          label="Thursday"
          name="thursday-checkbox"
          value={thursday}
          onChange={(value) => setThursday(value)}
          left
        />
        <Checkbox label="Friday" name="friday-checkbox" value={friday} onChange={(value) => setFriday(value)} left />
        <Checkbox
          label="Saturday"
          name="saturday-checkbox"
          value={saturday}
          onChange={(value) => setSaturday(value)}
          left
        />
        <Checkbox label="Sunday" name="sunday-checkbox" value={sunday} onChange={(value) => setSunday(value)} left />
      </div>
    </div>
  );

  const renderMonthlyOptions = () => (
    <div className="accountability-attendance-sessions-modal__monthly">
      <div className="accountability-attendance-sessions-modal__monthly__option">
        <div
          className="accountability-attendance-sessions-modal__radio-button accountability-attendance-sessions-modal__radio-button--outer"
          onClick={() => setSpecificDayInMonth(true)}
          data-testid="monthly-radio-option-one-outer"
        >
          {specificDayInMonth && (
            <div
              className="accountability-attendance-sessions-modal__radio-button accountability-attendance-sessions-modal__radio-button--inner"
              data-testid="monthly-radio-option-one-inner"
            />
          )}
        </div>
        <p>Day</p>
        <NumberPicker value={dayInMonth} onChange={(value) => setDayInMonth(value.toString())} hideArrows />
        <p>of every</p>
        <NumberPicker
          value={monthlyFrequencyOne}
          onChange={(value) => setMonthlyFrequencyOne(value.toString())}
          hideArrows
        />
        <p>month(s)</p>
      </div>
      <div className="accountability-attendance-sessions-modal__monthly__option">
        <div
          className="accountability-attendance-sessions-modal__radio-button accountability-attendance-sessions-modal__radio-button--outer"
          onClick={() => setSpecificDayInMonth(false)}
          data-testid="monthly-radio-option-two-outer"
        >
          {!specificDayInMonth && (
            <div
              className="accountability-attendance-sessions-modal__radio-button accountability-attendance-sessions-modal__radio-button--inner"
              data-testid="monthly-radio-option-two-inner"
            />
          )}
        </div>
        <p>The</p>
        <Dropdown
          value={{ label: monthlyStringOne, value: monthlyStringOne }}
          onChange={(option) => setMonthlyStringOne(option.value)}
          options={mapStringOptionsToObjectOptions(['First', 'Second', 'Third', 'Fourth', 'Last'])}
          inline
        />
        <Dropdown
          value={{ label: monthlyStringTwo, value: monthlyStringTwo }}
          onChange={(option) => setMonthlyStringTwo(option.value)}
          options={mapStringOptionsToObjectOptions([
            'Weekday',
            'Weekend Day',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
            'Sunday'
          ])}
          inline
        />
        <p>of every</p>
        <NumberPicker
          value={monthlyFrequencyTwo}
          onChange={(value) => setMonthlyFrequencyTwo(value.toString())}
          hideArrows
        />
        <p>month(s)</p>
      </div>
    </div>
  );

  return (
    <Modal show={show}>
      <div className="accountability-attendance-sessions-modal">
        <div className="accountability-attendance-sessions-modal__header">
          {session ? 'Edit Session' : 'Add Sessions'}
        </div>
        {!session && (
          <div className="accountability-attendance-sessions-modal__content">
            <div className="accountability-attendance-sessions-modal__session-name">
              <label htmlFor="session-name">Session Name</label>
              <TextInput
                value={templateName}
                onChange={({ target }) => setTemplateName(target.value)}
                name="session-name"
              />
            </div>
            <div className="accountability-attendance-sessions-modal__session-time">
              <p>Session Time</p>
              <div>
                <label htmlFor="start-time">From</label>
                <Dropdown
                  value={startTime ? { label: startTime, value: startTime } : undefined}
                  onChange={(option) => setStartTime(option.value)}
                  options={startTimeOptions}
                  name="start-time"
                  inline
                />
                <label htmlFor="end-time">To</label>
                <Dropdown
                  value={endTime ? { label: endTime, value: endTime } : undefined}
                  onChange={(option) => setEndTime(option.value)}
                  options={endTimeOptions}
                  name="end-time"
                  inline
                />
              </div>
            </div>
            <div className="accountability-attendance-sessions-modal__reoccurrence-pattern">
              <label htmlFor="reoccurrence-pattern">Reoccurrence Pattern</label>
              <Dropdown
                value={reoccurrencePattern}
                onChange={(option) => setReoccurrencePattern(option)}
                options={REOCCURRENCE_OPTIONS}
                name="reoccurrence-pattern"
                inline
              />
            </div>

            {reoccurrencePattern.value === 'daily' && renderDailyOptions()}

            {reoccurrencePattern.value === 'weekly' && renderWeeklyOptions()}

            {reoccurrencePattern.value === 'monthly' && renderMonthlyOptions()}

            <div className="accountability-attendance-sessions-modal__dates">
              <div>
                <label htmlFor="start-date">{reoccurrencePattern.value === 'once-off' ? 'Date' : 'From Date'}</label>
                <DatePicker value={startDate} onChange={(date) => setStartDate(date)} name="start-date" />
              </div>
              {reoccurrencePattern.value !== 'once-off' && (
                <div>
                  <label htmlFor="end-date">To Date</label>
                  <DatePicker value={endDate} onChange={(date) => setEndDate(date)} name="end-date" />
                </div>
              )}
            </div>
          </div>
        )}

        {session && (
          <div className="accountability-attendance-sessions-modal__content">
            <div className="accountability-attendance-sessions-modal__session-name">
              <label htmlFor="edit-name">Session Name</label>
              <TextInput
                value={name}
                onChange={({ target }) => setName(target.value)}
                name="edit-name"
                disabled={status === ARCHIVED}
              />
            </div>

            <div className="accountability-attendance-sessions-modal__session-time">
              <p>Session Time</p>
              <div>
                <label htmlFor="edit-from-time">From</label>
                <Dropdown
                  value={startTime ? { label: startTime, value: startTime } : undefined}
                  onChange={(option) => setStartTime(option.value)}
                  options={startTimeOptions}
                  name="edit-from-time"
                  disabled={status === ARCHIVED}
                  inline
                />
                <label htmlFor="edit-to-time">To</label>
                <Dropdown
                  value={endTime ? { label: endTime, value: endTime } : undefined}
                  onChange={(option) => setEndTime(option.value)}
                  options={endTimeOptions}
                  name="edit-to-time"
                  disabled={status === ARCHIVED}
                  inline
                />
              </div>
            </div>

            <div className="accountability-attendance-sessions-modal__dates">
              <div>
                <label htmlFor="edit-date">Date</label>
                <DatePicker
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  name="edit-date"
                  disabled={status === ARCHIVED}
                />
              </div>
            </div>
          </div>
        )}

        <div className="accountability-attendance-sessions-modal__button-container">
          <Button text={status === ARCHIVED ? 'Close' : 'Cancel'} onClick={closeModal} />
          {status !== ARCHIVED && <Button text="Save" onClick={handleSaveClick} />}
        </div>
      </div>
    </Modal>
  );
};

export default AccountabilityAttendanceSessionsModal;
